import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container, AppBar, Toolbar, Typography, Box, Drawer, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import Tooltip from '@mui/material/Tooltip';
import Sidebar from './components/Sidebar';
import AlphaStamp from './components/AlphaStamp';
import { AuthProvider } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import UserMenu from './components/auth/UserMenu';
import HomePage from './components/HomePage';
import SimpleSearch from './components/SimpleSearch';
import ParliamentariansSearch from './components/ParliamentariansSearch';
import ParliamentarianDetail from './components/ParliamentarianDetail';
import CommitteesSearch from './components/CommitteesSearch';
import CommitteeDetail from './components/CommitteeDetail';
import ElectoratesSearch from './components/ElectoratesSearch';
import ElectorateDetail from './components/ElectorateDetail';
import ElectorateMapFullscreen from './components/ElectorateMapFullscreen';
import MinistriesSearch from './components/MinistriesSearch';
import MinistryDetail from './components/MinistryDetail';
import ParliamentsSearch from './components/ParliamentsSearch';
import ParliamentDetail from './components/ParliamentDetail';
import PartiesSearch from './components/PartiesSearch';
import PartyDetail from './components/PartyDetail';
import ElectionsSearch from './components/ElectionsSearch';
import ElectionDetail from './components/ElectionDetail';
import MinistryRecordsSearch from './components/MinistryRecordsSearch';
import ShadowMinistriesSearch from './components/ShadowMinistriesSearch';
import ShadowMinistryDetail from './components/ShadowMinistryDetail';
import ShadowMinistryRecordsSearch from './components/ShadowMinistryRecordsSearch';
import RecordsOfServiceSearch from './components/RecordsOfServiceSearch';
import HansardSearch from './components/HansardSearch';
import HansardDetail from './components/HansardDetail';
import ConstitutionSearch from './components/ConstitutionSearch';
import AustraliaActSearch from './components/AustraliaActSearch';
import SenateQonSearch from './components/SenateQonSearch';
import SenateQonDetail from './components/SenateQonDetail';
import BillsSearch from './components/BillsSearch';
import BillDetail from './components/BillDetail';
import HouseQiwSearch from './components/HouseQiwSearch';
import CalendarSearch from './components/CalendarSearch';
import TabledDocumentsSearch from './components/TabledDocumentsSearch';
import TabledDocumentDetail from './components/TabledDocumentDetail';
import DivisionsSearch from './components/DivisionsSearch';
import DivisionDetail from './components/DivisionDetail';
import HouseNoticePapersSearch from './components/HouseNoticePapersSearch';
import SenateNoticePapersSearch from './components/SenateNoticePapersSearch';
import NoticePaperDetail from './components/NoticePaperDetail';
import StandingOrdersSearch from './components/StandingOrdersSearch';
import StandingOrdersDetail from './components/StandingOrdersDetail';
import StandingOrdersCompare from './components/StandingOrdersCompare';
import SenateStandingOrdersSearch from './components/SenateStandingOrdersSearch';
import HousePracticeSearch from './components/HousePracticeSearch';
import SenateDivisionsSearch from './components/SenateDivisionsSearch';
import HouseDailyProgramSearch from './components/HouseDailyProgramSearch';
import SenateOrderOfBusinessSearch from './components/SenateOrderOfBusinessSearch';
import SenateJournalsSearch from './components/SenateJournalsSearch';
import SenateJournalsDetail from './components/SenateJournalsDetail';
import VotesProceedingsSearch from './components/VotesProceedingsSearch';
import VotesProceedingsDetail from './components/VotesProceedingsDetail';
import LiveMinutesSearch from './components/LiveMinutesSearch';
import LiveMinutesDetail from './components/LiveMinutesDetail';
import HouseQuestionsSearch from './components/HouseQuestionsSearch';
import HouseQuestionsDetail from './components/HouseQuestionsDetail';
import NoticesOfMotionSearch from './components/NoticesOfMotionSearch';
import MotionDetail from './components/MotionDetail';
import CommitteeReportsSearch from './components/CommitteeReportsSearch';
import CommitteeReportDetail from './components/CommitteeReportDetail';
import PetitionsSearch from './components/PetitionsSearch';
import DataTypeCarousel from './components/DataTypeCarousel';
import ExploreStatsHub from './components/explore/ExploreStatsHub';
import SittingPatternStats from './components/explore/SittingPatternStats';
import GenderComposition from './components/explore/GenderComposition';
import AgeGroups from './components/explore/AgeGroups';
import ParliamentarianAges from './components/explore/ParliamentarianAges';
import OverseasBorn from './components/explore/OverseasBorn';
import FieldsOfQualifications from './components/explore/FieldsOfQualifications';
import LevelsOfQualifications from './components/explore/LevelsOfQualifications';
import PreviousOccupations from './components/explore/PreviousOccupations';
import LengthOfServiceGroups from './components/explore/LengthOfServiceGroups';
import PeriodsOfService from './components/explore/PeriodsOfService';
import WomenInParliament from './components/explore/WomenInParliament';
import LongestServingMembers from './components/explore/LongestServingMembers';
import PartyRepresentation from './components/explore/PartyRepresentation';
import PartyRepresentationByState from './components/explore/PartyRepresentationByState';
import FirstNations from './components/explore/FirstNations';
import MilitaryService from './components/explore/MilitaryService';
import ChangedElectorate from './components/explore/ChangedElectorate';
import CommitteeStatistics from './components/explore/CommitteeStatistics';
import CommitteeMembership from './components/explore/CommitteeMembership';
import DivisionStatistics from './components/explore/DivisionStatistics';
import LegislationPassage from './components/explore/LegislationPassage';
import MinisterialStatements from './components/explore/MinisterialStatements';
import ParliamentaryPartyStatistics from './components/explore/ParliamentaryPartyStatistics';
import PartyTimeline from './components/explore/PartyTimeline';
import ChangedParties from './components/explore/ChangedParties';
import PrimeMinisters from './components/explore/PrimeMinisters';
import OppositionLeaders from './components/explore/OppositionLeaders';
import Speakers from './components/explore/Speakers';
import Presidents from './components/explore/Presidents';
import QtOverview from './components/explore/QtOverview';
import QtPeople from './components/explore/QtPeople';
import QtLength from './components/explore/QtLength';
import QtClock from './components/explore/QtClock';
import SittingDayLength from './components/explore/SittingDayLength';
import BillsOnTheFloor from './components/explore/BillsOnTheFloor';
import OutcomesLedger from './components/explore/OutcomesLedger';
import BothChambers from './components/explore/BothChambers';
import OfficeHolders from './components/explore/OfficeHolders';
import RoyalCommissions from './components/explore/RoyalCommissions';
import HeadsOfState from './components/explore/HeadsOfState';
import CasualVacancies from './components/explore/CasualVacancies';
import Referendums from './components/explore/Referendums';
import Elections from './components/explore/Elections';
import FirstMostMore from './components/explore/FirstMostMore';
import QtPortfolios from './components/explore/QtPortfolios';
import QtInterjections from './components/explore/QtInterjections';
import TimeOnBusiness from './components/explore/TimeOnBusiness';
import PriorElectedService from './components/explore/PriorElectedService';
import Help from './components/Help';
import GuidedSearch from './components/GuidedSearch';
import FeatureRequestPage from './components/FeatureRequestPage';
import MyFeatureRequestsPage from './components/MyFeatureRequestsPage';
import Documentation from './components/Documentation';
import DataSources from './components/DataSources';
import ChangelogPage from './components/ChangelogPage';
import LegalPage from './components/LegalPage';
import NotFound from './components/NotFound';
import Acknowledgements from './components/Acknowledgements';
import { SavedSearchesPage } from './components/saved-searches';
import { SettingsPage } from './components/settings';
import { AlertsPage } from './components/alerts';
import './App.css';

// Lazy load GraphVisualizer to prevent potential issues with force-graph from blocking app load
const GraphVisualizer = lazy(() => import('./components/GraphVisualizer'));

const DRAWER_WIDTH = 260;

// Header control to switch between light and dark themes (#159). Reflects the
// resolved mode and flips to the opposite on click; the choice is persisted via
// PreferencesContext (backend for signed-in users, localStorage otherwise).
function ThemeToggle() {
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <Tooltip title={label}>
      <IconButton
        color="inherit"
        aria-label={label}
        onClick={toggleMode}
        sx={{ ml: 0.5 }}
      >
        {isDark ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer on navigation
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Handle escape key to close mobile drawer
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && mobileOpen) {
      setMobileOpen(false);
    }
  }, [mobileOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Skip link for accessibility */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          padding: 2,
          bgcolor: 'primary.main',
          color: 'white',
          textDecoration: 'none',
          '&:focus': {
            left: 0,
            top: 0,
          }
        }}
      >
        Skip to main content
      </Box>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          color: 'text.primary',
          borderBottom: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}`,
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Box
            sx={{ flexGrow: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5 }}
            onClick={() => navigate('/')}
          >
            {/* Ledger logo: accent dot + heavy wordmark */}
            <Box
              component="span"
              sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }}
            />
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: '1.2rem',
                letterSpacing: '-0.03em',
                color: 'text.primary'
              }}
            >
              ParlSearch
            </Typography>
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8125rem',
                fontWeight: 500,
                letterSpacing: 0,
                display: { xs: 'none', sm: 'inline' }
              }}
            >
              An independent index
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
              <AlphaStamp size={28} />
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'ledger.accentText' } }}
            onClick={() => navigate('/help')}
          >
            Help
          </Typography>
          <ThemeToggle />
          <UserMenu />
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Sidebar onClose={handleDrawerClose} isMobile={true} />
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          },
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Sidebar />
      </Drawer>

      <Box
        id="main-content"
        component="main"
        sx={{
          flexGrow: 1,
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/saved-searches" element={<SavedSearchesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/questions" element={<SimpleSearch />} />
            <Route path="/parliamentarians" element={<ParliamentariansSearch />} />
            <Route path="/parliamentarians/:phid" element={<ParliamentarianDetail />} />
            <Route path="/committees" element={<CommitteesSearch />} />
            <Route path="/committees/:cid" element={<CommitteeDetail />} />
            <Route path="/electorates" element={<ElectoratesSearch />} />
            <Route path="/electorates/:eid" element={<ElectorateDetail />} />
            <Route path="/ministries" element={<MinistriesSearch />} />
            <Route path="/ministries/:ministryId" element={<MinistryDetail />} />
            <Route path="/parliaments" element={<ParliamentsSearch />} />
            <Route path="/parliaments/:pid" element={<ParliamentDetail />} />
            <Route path="/parties" element={<PartiesSearch />} />
            <Route path="/parties/:pid" element={<PartyDetail />} />
            <Route path="/elections" element={<ElectionsSearch />} />
            <Route path="/elections/:eid" element={<ElectionDetail />} />
            <Route path="/ministryrecords" element={<MinistryRecordsSearch />} />
            <Route path="/shadowministries" element={<ShadowMinistriesSearch />} />
            <Route path="/shadowministries/:shadowMinistryId" element={<ShadowMinistryDetail />} />
            <Route path="/shadowministryrecords" element={<ShadowMinistryRecordsSearch />} />
            <Route path="/recordsofservice" element={<RecordsOfServiceSearch />} />
            <Route path="/hansard" element={<HansardSearch />} />
            <Route path="/hansard/:id" element={<HansardDetail />} />
            <Route path="/constitution" element={<ConstitutionSearch />} />
            <Route path="/australiaact" element={<AustraliaActSearch />} />
            <Route path="/senateqon" element={<SenateQonSearch />} />
            <Route path="/senateqon/:id" element={<SenateQonDetail />} />
            <Route path="/bills" element={<BillsSearch />} />
            <Route path="/bills/:id" element={<BillDetail />} />
            <Route path="/houseqiw" element={<HouseQiwSearch />} />
            <Route path="/calendar" element={<CalendarSearch />} />
            <Route path="/tableddocuments" element={<TabledDocumentsSearch />} />
            <Route path="/tableddocuments/:id" element={<TabledDocumentDetail />} />
            <Route path="/divisions" element={<DivisionsSearch />} />
            <Route path="/divisions/:id" element={<DivisionDetail chamber="House" />} />
            <Route path="/house-notice-papers" element={<HouseNoticePapersSearch />} />
            <Route path="/house-notice-papers/:id" element={<NoticePaperDetail chamber="House" />} />
            <Route path="/senate-notice-papers" element={<SenateNoticePapersSearch />} />
            <Route path="/senate-notice-papers/:id" element={<NoticePaperDetail chamber="Senate" />} />
            <Route path="/standingorders" element={<StandingOrdersSearch />} />
            <Route path="/standingorders/compare" element={<StandingOrdersCompare />} />
            <Route path="/standingorders/:soNumber" element={<StandingOrdersDetail />} />
            <Route path="/senate-standingorders" element={<SenateStandingOrdersSearch />} />
            <Route path="/house-practice" element={<HousePracticeSearch />} />
            <Route path="/senate-divisions" element={<SenateDivisionsSearch />} />
            <Route path="/senate-divisions/:id" element={<DivisionDetail chamber="Senate" />} />
            <Route path="/house-daily-program" element={<HouseDailyProgramSearch />} />
            <Route path="/senate-order-of-business" element={<SenateOrderOfBusinessSearch />} />
            <Route path="/senate-journals" element={<SenateJournalsSearch />} />
            <Route path="/senate-journals/:id" element={<SenateJournalsDetail />} />
            <Route path="/votes-proceedings" element={<VotesProceedingsSearch />} />
            <Route path="/votes-proceedings/:id" element={<VotesProceedingsDetail />} />
            <Route path="/live-minutes" element={<LiveMinutesSearch />} />
            <Route path="/live-minutes/:id" element={<LiveMinutesDetail />} />
            <Route path="/house-questions" element={<HouseQuestionsSearch />} />
            <Route path="/house-questions/:id" element={<HouseQuestionsDetail />} />
            <Route path="/motions" element={<NoticesOfMotionSearch />} />
            <Route path="/motions/:id" element={<MotionDetail />} />
            <Route path="/committee-reports" element={<CommitteeReportsSearch />} />
            <Route path="/committee-reports/:reportId" element={<CommitteeReportDetail />} />
            <Route path="/petitions" element={<PetitionsSearch />} />
            <Route path="/graph" element={<Suspense fallback={<Box sx={{ p: 4, textAlign: 'center' }}>Loading graph...</Box>}><GraphVisualizer /></Suspense>} />
            <Route path="/explore" element={<DataTypeCarousel />} />
            <Route path="/explore/stats" element={<ExploreStatsHub />} />
            <Route path="/explore/stats/sitting-patterns" element={<SittingPatternStats />} />
            <Route path="/explore/stats/sitting-day-length" element={<SittingDayLength />} />
            <Route path="/explore/stats/gender-composition" element={<GenderComposition />} />
            <Route path="/explore/stats/age-groups" element={<AgeGroups />} />
            <Route path="/explore/stats/parliamentarian-ages" element={<ParliamentarianAges />} />
            <Route path="/explore/stats/overseas-born" element={<OverseasBorn />} />
            <Route path="/explore/stats/fields-of-qualifications" element={<FieldsOfQualifications />} />
            <Route path="/explore/stats/levels-of-qualifications" element={<LevelsOfQualifications />} />
            <Route path="/explore/stats/previous-occupations" element={<PreviousOccupations />} />
            <Route path="/explore/stats/length-of-service-groups" element={<LengthOfServiceGroups />} />
            <Route path="/explore/stats/periods-of-service" element={<PeriodsOfService />} />
            <Route path="/explore/stats/women-in-parliament" element={<WomenInParliament />} />
            <Route path="/explore/stats/longest-serving-members" element={<LongestServingMembers />} />
            <Route path="/explore/stats/party-representation" element={<PartyRepresentation />} />
            <Route path="/explore/stats/party-representation-by-state" element={<PartyRepresentationByState />} />
            <Route path="/explore/stats/first-nations" element={<FirstNations />} />
            <Route path="/explore/stats/military-service" element={<MilitaryService />} />
            <Route path="/explore/stats/changed-electorate" element={<ChangedElectorate />} />
            <Route path="/explore/stats/committee-statistics" element={<CommitteeStatistics />} />
            <Route path="/explore/stats/committee-membership" element={<CommitteeMembership />} />
            <Route path="/explore/stats/division-statistics" element={<DivisionStatistics />} />
            <Route path="/explore/stats/legislation-passage" element={<LegislationPassage />} />
            <Route path="/explore/stats/ministerial-statements" element={<MinisterialStatements />} />
            <Route path="/explore/stats/parliamentary-party-statistics" element={<ParliamentaryPartyStatistics />} />
            <Route path="/explore/stats/party-timeline" element={<PartyTimeline />} />
            <Route path="/explore/stats/changed-parties" element={<ChangedParties />} />
            <Route path="/explore/stats/prime-ministers" element={<PrimeMinisters />} />
            <Route path="/explore/stats/opposition-leaders" element={<OppositionLeaders />} />
            <Route path="/explore/stats/speakers" element={<Speakers />} />
            <Route path="/explore/stats/presidents" element={<Presidents />} />
            <Route path="/explore/stats/office-holders" element={<OfficeHolders />} />
            <Route path="/explore/stats/royal-commissions" element={<RoyalCommissions />} />
            <Route path="/explore/stats/heads-of-state" element={<HeadsOfState />} />
            <Route path="/explore/stats/casual-vacancies" element={<CasualVacancies />} />
            <Route path="/explore/stats/referendums" element={<Referendums />} />
            <Route path="/explore/stats/elections" element={<Elections />} />
            <Route path="/explore/stats/qt-overview" element={<QtOverview />} />
            <Route path="/explore/stats/qt-people" element={<QtPeople />} />
            <Route path="/explore/stats/qt-length" element={<QtLength />} />
            <Route path="/explore/stats/qt-clock" element={<QtClock />} />
            <Route path="/explore/stats/qt-portfolios" element={<QtPortfolios />} />
            <Route path="/explore/stats/qt-interjections" element={<QtInterjections />} />
            <Route path="/explore/stats/time-on-business" element={<TimeOnBusiness />} />
            <Route path="/explore/stats/bills-on-the-floor" element={<BillsOnTheFloor />} />
            <Route path="/explore/stats/outcomes-ledger" element={<OutcomesLedger />} />
            <Route path="/explore/stats/both-chambers" element={<BothChambers />} />
            <Route path="/explore/stats/first-most-more" element={<FirstMostMore />} />
            <Route path="/explore/stats/prior-elected-service" element={<PriorElectedService />} />
            <Route path="/help" element={<Help />} />
            <Route path="/guided-search" element={<GuidedSearch />} />
            <Route path="/feature-request" element={<FeatureRequestPage />} />
            <Route path="/my-requests" element={<MyFeatureRequestsPage />} />
            <Route path="/data-sources" element={<DataSources />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/doco" element={<Documentation />} />
            <Route path="/terms" element={<LegalPage doc="terms" />} />
            <Route path="/privacy" element={<LegalPage doc="privacy" />} />

            {/* "Coming soon" datasets — disabled in the sidebar nav but
                reachable by direct/bookmarked URL; show an explanatory
                page instead of silent blank content (#137). */}
            {[
              '/senate-motions', '/committee-hansard', '/press-releases',
              '/newspaper-clippings', '/radio-tv', '/library-publications',
              '/parliamentary-papers', '/budget-papers', '/education',
              '/statute-of-westminster', '/federal-conventions',
            ].map((p) => (
              <Route key={p} path={p} element={<NotFound mode="coming-soon" />} />
            ))}

            <Route path="/acknowledgements" element={<Acknowledgements />} />
            {/* Catch-all 404 for any other unknown path. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Container>

        <Box
          component="footer"
          sx={{
            py: 2.5,
            px: 4,
            bgcolor: 'background.default',
            borderTop: (theme) => `${theme.palette.ledger.ruleWidth} solid ${theme.palette.ledger.ruleColor}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: 'text.secondary',
            }}
          >
            ParlSearch — An independent index
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: 'text.secondary',
            }}
          >
            Data: Parliament of Australia ·{' '}
            <a
              href="https://api.parlsearch.cc/api/docs"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}
            >
              API
            </a>
            {' · '}
            <RouterLink
              to="/acknowledgements"
              style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}
            >
              Acknowledgements
            </RouterLink>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function AppRouter() {
  const location = useLocation();

  // Fullscreen routes that don't show the sidebar
  const fullscreenRoutes = ['/electorates/map'];
  const isFullscreen = fullscreenRoutes.some(route => location.pathname.startsWith(route));

  if (isFullscreen) {
    return (
      <Routes>
        <Route path="/electorates/map" element={<ElectorateMapFullscreen />} />
      </Routes>
    );
  }

  return <AppContent />;
}

function App() {
  return (
    <AuthProvider>
      <PreferencesProvider>
        <ThemeModeProvider>
          <AppRouter />
        </ThemeModeProvider>
      </PreferencesProvider>
    </AuthProvider>
  );
}

export default App;
